/* eslint-disable react/display-name */
import CheckIcon from '@mui/icons-material/Check'
import {
	BaseSelectProps,
	FormControl,
	FormHelperText,
	InputLabel,
	Select,
} from '@mui/material'
import { ForwardedRef, forwardRef, ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import * as S from './styles'

type IProps = Omit<BaseSelectProps, 'multiple' | 'value'> & {
	handleChange: BaseSelectProps['onChange']
	label?: string | ReactNode
	options: Record<string, string>
	disabled?: boolean
	required?: boolean
	fullWidth?: boolean
	fieldError?: string
} & (
		| { multiple: true; value: readonly string[] }
		| { multiple?: false; value?: string }
	)

const Dropdown = forwardRef(
	(props: IProps, ref: ForwardedRef<HTMLDivElement>) => {
		const { t } = useTranslation()
		const {
			options,
			value,
			handleChange,
			label,
			disabled,
			multiple,
			required,
			fullWidth,
			fieldError,
			...rest
		} = props

		const handleRenderMultipleValues = useCallback(
			(selected: unknown): ReactNode =>
				(selected as readonly string[])
					.map(value => options?.[value])
					.join(', '),
			[options]
		)
		return (
			<FormControl
				error={!!fieldError}
				variant={rest.variant || 'filled'}
				required={required}
				style={{ width: fullWidth ? '100%' : 'auto' }}
				ref={ref}
			>
				<InputLabel>{label}</InputLabel>
				<Select
					renderValue={multiple ? handleRenderMultipleValues : undefined}
					{...rest}
					label={label}
					value={value ?? ''}
					onChange={handleChange}
					disabled={disabled}
					multiple={multiple}
				>
					{options &&
						Object.keys(options).map(option => (
							<S.MenuItem
								key={option}
								value={option}
								style={{ whiteSpace: 'break-spaces' }}
								$multiple={multiple}
							>
								{options[option]}
								{multiple && value.includes(option) && (
									<CheckIcon color="success" fontSize="inherit" />
								)}
							</S.MenuItem>
						))}
				</Select>
				{fieldError === 'required' && (
					<FormHelperText style={{ marginLeft: '-5px', marginBottom: '-20px' }}>
						{t('formField.required')}
					</FormHelperText>
				)}
			</FormControl>
		)
	}
)

export { Dropdown }
